import type { ChannelSlice } from '../ChannelSlice';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { getActiveColumnFilters } from './activeColumnFilters';
import type { ColumnFilters } from './filters';
import { batch, computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { MutationObserver } from '@tanstack/query-core';
import { requestIdHeader } from '@thinkalpha/common/contracts/x-headers.js';
import { EmptyArray } from '@thinkalpha/common/util/iterators.js';
import { doesAccessGrantAllowRequest } from '@thinkalpha/common/util/permissions.js';
import type { ConcreteAccount } from '@thinkalpha/trading-ws-client/contracts/account.js';
import { deferred } from '@thinkalpha/ui-core/deferred';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import type { AutoAcceptInfo, LocateOrder, NewLocateOrder } from '@thinkalpha/ui-types/contracts/locates/orders';
import type { LocateAccount } from '@thinkalpha/ui-types/contracts/locates/platforms';
import type { LocateProvider } from '@thinkalpha/ui-types/contracts/locates/providers';
import type { LocatesV2FormData, QuickSendButton } from '@thinkalpha/ui-types/contracts/workspace';
import { isAxiosError } from 'axios';
import { isEqual } from 'es-toolkit';
import { optional } from 'inversify';
import { from } from 'rxjs';
import type { TableStatus } from 'src/components/table-view/model';
import { ReactiveInjectable, inject, injectable, reacts } from 'src/features/ioc';
import { getAccountsForUserQuery } from 'src/hooks/accounts/useAccounts';
import {
    addColumnFilter,
    removeColumnFilter,
    clearColumnFilter,
    clearAllColumnFilters,
    columnFilterReducer,
} from 'src/lib/columnFilters/columnFilters';
import type { ColumnFilter } from 'src/lib/columnFilters/columnFilters';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { enableLab2AutoAccept } from 'src/lib/featureFlags/flags/enableLab2AutoAccept';
import { enableLabAutoAcceptPercent } from 'src/lib/featureFlags/flags/enableLabAutoAcceptPercent';
import { locatesV2UserOnlyFiltering } from 'src/lib/featureFlags/flags/locatesV2UserOnlyFiltering';
import type {
    AutoAcceptParams,
    LocatesV2InitializingWidgetState,
    LocatesV2ReadyWidgetState,
    LocatesV2WidgetModel,
    LocatesV2WidgetModelState,
    LocatesV2WidgetModelViewState,
} from 'src/models/LocatesV2WidgetModel';
import { LocatesInjectionModel } from 'src/models/LocatesV2WidgetModel/LocatesInjectionModel';
import { LocatesRowDataSourceWorkerProvider } from 'src/models/LocatesV2WidgetModel/LocatesRowDataSourceWorkerProvider';
import { log } from 'src/models/LocatesV2WidgetModel/log';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { combineUserAndLocatesAccounts, getLocatesAccountsQuery, getLocatesProvidersQuery } from 'src/queries/locates';
import { DEFAULT_LOCATES_V2_SORT } from 'src/queries/locatesV2/sorts';
import type { LocatesV2Sort } from 'src/queries/locatesV2/sorts';
import { createLocatesOrderMutation } from 'src/queries/locatesV2Mutations';
import type { CreateLocatesOrderParams } from 'src/queries/locatesV2Mutations';
import type { LocatesV2ColumnId } from 'src/routes/widgets/LocatesV2Widget/columns';
import {
    DEFAULT_COLUMN_ORDER,
    DEFAULT_COLUMN_VISIBILITY,
    DEFAULT_COLUMN_WIDTHS,
} from 'src/routes/widgets/LocatesV2Widget/columns';
import type { LocatesActivity } from 'src/services/LocatesWorkerService/contract';
import type { MessagesService } from 'src/services/MessagesService';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import {
    setLocatesV2AccountIds,
    setLocatesV2DisplayingCostInfo,
    setLocatesV2DisplayingQuote,
    setLocatesV2DisplayingResults,
    setLocatesV2OnlyPending,
    setLocatesV2OnlyResults,
    setLocatesV2OnlyUserActivity,
    setLocatesV2QuantityKilo,
    setLocatesV2SoundAlert,
    setLocatesV2UserOnly,
    storeLocatesV2SplitPercentage,
    updateLocatesV2CostInfoColumnTemplate,
    updateLocatesV2Filter,
    updateLocatesV2FormData,
    updateLocatesV2QuickSendButtons,
    updateLocatesV2ColumnWidths,
    updateLocatesV2ColumnOrder,
    updateLocatesV2ColumnVisibility,
    updateLocatesV2Sort,
    setLocatesV2AutoAcceptType,
    setLocatesV2AutoAcceptPercent,
} from 'src/store/actions/widgets/locatesV2';
import { createDefaultLocatesV2Widget } from 'src/store/defaults/widget';
import { getLocatesSocketConnectionSettingsFromState } from 'src/store/selectors/locates/locatesSocket';
import type { LocatesV2WidgetViewModel } from 'src/store/types';
import type { ReactBindings } from 'src/types/bindings';
import type { ConnectionStatusSignal } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

/**
 * Creates a ready widget state from an initializing widget state.
 * Handles the transition from initializing to ready by creating all necessary signals and computed values.
 */
export function makeLocatesV2ReadyWidgetState(
    initializingState: LocatesV2InitializingWidgetState,
): LocatesV2ReadyWidgetState {
    const userAccountsSignal = signal<ConcreteAccount[]>(initializingState.userAccounts!);
    const locatesAccountsSignal = signal<LocateAccount[]>(initializingState.locatesAccounts!);
    const accountIdsSignal = initializingState.accountIds;

    const combinedAccountsSignal = computed(() =>
        combineUserAndLocatesAccounts(userAccountsSignal.value, locatesAccountsSignal.value),
    );

    // Compute "good" account IDs - accounts that exist in BOTH userAccounts and locatesAccounts
    // This excludes accounts that only exist in locatesAccounts (e.g., superuser-visible accounts)
    const goodAccountIdsSignal = computed(() => {
        const users = userAccountsSignal.value;
        const locates = locatesAccountsSignal.value;

        // Return IDs of accounts that exist in both lists
        return users.filter((userAcc) => locates.some((locAcc) => locAcc.id === userAcc.id)).map((acc) => acc.id);
    });

    const selectedAccountsSignal = computed(() => {
        const combined = combinedAccountsSignal.value;
        const ids = accountIdsSignal.value;
        const goodIds = goodAccountIdsSignal.value;

        // If no account IDs selected (null or empty), filter to only "good" accounts
        // This matches the old widget behavior of only showing accounts the user has access to
        if (!ids || ids.length === 0) {
            return combined.filter((acc) => goodIds.includes(acc.id));
        }

        // Otherwise, filter to only the selected account IDs
        return combined.filter((acc) => ids.includes(acc.id));
    });

    const readerPermissionAccountsSignal = computed(() =>
        selectedAccountsSignal.value.filter((acc) =>
            doesAccessGrantAllowRequest(acc.permissions.effectiveAccess, 'reader'),
        ),
    );

    const nonLocatesAccountIdsSignal = computed(() =>
        userAccountsSignal.value
            .filter((userAcc) => !locatesAccountsSignal.value.some((locAcc) => locAcc.id === userAcc.id))
            .map((acc) => acc.id),
    );

    return {
        type: 'ready',
        isShowingQuote: initializingState.isShowingQuote,
        isShowingResults: initializingState.isShowingResults,
        isShowingCostInfo: initializingState.isShowingCostInfo,
        splitPercentage: initializingState.splitPercentage,
        formData: initializingState.formData,
        quickSendButtons: initializingState.quickSendButtons,
        isSoundAlertEnabled: initializingState.isSoundAlertEnabled,
        isQuantityKilo: initializingState.isQuantityKilo,
        isUserOnly: initializingState.isUserOnly,
        onlyPending: initializingState.onlyPending,
        onlyResults: initializingState.onlyResults,
        onlyUserActivity: initializingState.onlyUserActivity,
        accountIds: accountIdsSignal,
        filter: initializingState.filter,
        costInfoColumnTemplate: initializingState.costInfoColumnTemplate,
        userAccounts: userAccountsSignal,
        locatesAccounts: locatesAccountsSignal,
        providers: signal<LocateProvider[]>(initializingState.providers!),
        quoteProviders: signal<LocateProvider[]>(initializingState.quoteProviders!),
        combinedAccounts: combinedAccountsSignal,
        selectedAccounts: selectedAccountsSignal,
        readerPermissionAccounts: readerPermissionAccountsSignal,
        nonLocatesAccountIds: nonLocatesAccountIdsSignal,
        columnOrder: initializingState.columnOrder,
        columnWidths: initializingState.columnWidths,
        columnVisibility: initializingState.columnVisibility,
        currentSort: initializingState.currentSort,
        autoAcceptType: initializingState.autoAcceptType,
        autoAcceptPercent: initializingState.autoAcceptPercent,
    };
}

/**
 * Actions for updating LocatesV2WidgetModel internal state
 */
type LocatesV2WidgetModelAction =
    | { type: 'UPDATE_DISPLAYING_QUOTE'; value: boolean }
    | { type: 'UPDATE_DISPLAYING_RESULTS'; value: boolean }
    | { type: 'UPDATE_DISPLAYING_COST_INFO'; value: boolean }
    | { type: 'UPDATE_SPLIT_PERCENTAGE'; value: number }
    | { type: 'UPDATE_FORM_DATA'; value: LocatesV2FormData }
    | { type: 'UPDATE_QUICK_SEND_BUTTONS'; value: QuickSendButton[] }
    | { type: 'UPDATE_SOUND_ALERT'; value: boolean }
    | { type: 'UPDATE_QUANTITY_KILO'; value: boolean }
    | { type: 'UPDATE_USER_ONLY'; value: boolean }
    | { type: 'UPDATE_ONLY_PENDING'; value: boolean }
    | { type: 'UPDATE_ONLY_RESULTS'; value: boolean }
    | { type: 'UPDATE_ONLY_USER_ACTIVITY'; value: boolean }
    | { type: 'UPDATE_ACCOUNT_IDS'; value: string[] | null }
    | { type: 'UPDATE_FILTER'; value: string | null }
    | { type: 'UPDATE_COST_INFO_COLUMN_TEMPLATE'; value: ColumnTemplate | string | null }
    | { type: 'UPDATE_COLUMN_WIDTHS'; value: Record<string, number> }
    | { type: 'UPDATE_COLUMN_ORDER'; value: string[] }
    | { type: 'UPDATE_COLUMN_VISIBILITY'; value: Record<string, boolean> }
    | { type: 'UPDATE_CURRENT_SORT'; value: LocatesV2Sort }
    | { type: 'UPDATE_USER_ACCOUNTS'; value: ConcreteAccount[] }
    | { type: 'UPDATE_LOCATES_ACCOUNTS'; value: LocateAccount[] }
    | { type: 'UPDATE_PROVIDERS'; value: LocateProvider[] }
    | { type: 'UPDATE_AUTO_ACCEPT_TYPE'; value: 'price' | 'percent' }
    | { type: 'UPDATE_AUTO_ACCEPT_PERCENT'; value: number };

/**
 * Reducer function for managing LocatesV2WidgetModel state transitions
 * Mutates signals in-place for ready state, returns new state for transitions
 */
export function locatesV2WidgetModelReducer(
    state: LocatesV2WidgetModelState,
    action: LocatesV2WidgetModelAction,
): LocatesV2WidgetModelState {
    switch (action.type) {
        case 'UPDATE_DISPLAYING_QUOTE':
            if (state.type === 'ready') {
                state.isShowingQuote.value = action.value;
            }
            return state;
        case 'UPDATE_DISPLAYING_RESULTS':
            if (state.type === 'ready') {
                state.isShowingResults.value = action.value;
            }
            return state;
        case 'UPDATE_DISPLAYING_COST_INFO':
            if (state.type === 'ready') {
                state.isShowingCostInfo.value = action.value;
            }
            return state;
        case 'UPDATE_SPLIT_PERCENTAGE':
            if (state.type === 'ready') {
                state.splitPercentage.value = action.value;
            }
            return state;
        case 'UPDATE_FORM_DATA':
            if (state.type === 'ready') {
                state.formData.value = action.value;
            }
            return state;
        case 'UPDATE_QUICK_SEND_BUTTONS':
            if (state.type === 'ready') {
                state.quickSendButtons.value = action.value;
            }
            return state;
        case 'UPDATE_SOUND_ALERT':
            if (state.type === 'ready') {
                state.isSoundAlertEnabled.value = action.value;
            }
            return state;
        case 'UPDATE_QUANTITY_KILO':
            if (state.type === 'ready') {
                state.isQuantityKilo.value = action.value;
            }
            return state;
        case 'UPDATE_USER_ONLY':
            if (state.type === 'ready') {
                state.isUserOnly.value = action.value;
            }
            return state;
        case 'UPDATE_ONLY_PENDING':
            if (state.type === 'ready') {
                state.onlyPending.value = action.value;
            }
            return state;
        case 'UPDATE_ONLY_RESULTS':
            if (state.type === 'ready') {
                state.onlyResults.value = action.value;
            }
            return state;
        case 'UPDATE_ONLY_USER_ACTIVITY':
            if (state.type === 'ready') {
                state.onlyUserActivity.value = action.value;
            }
            return state;
        case 'UPDATE_ACCOUNT_IDS':
            if (state.type === 'ready') {
                state.accountIds.value = action.value;
            }
            return state;
        case 'UPDATE_FILTER':
            if (state.type === 'ready') {
                state.filter.value = action.value;
            }
            return state;
        case 'UPDATE_COST_INFO_COLUMN_TEMPLATE':
            if (state.type === 'ready') {
                state.costInfoColumnTemplate.value = action.value;
            }
            return state;
        case 'UPDATE_COLUMN_WIDTHS':
            if (state.type === 'ready') {
                state.columnWidths.value = action.value;
            }
            return state;
        case 'UPDATE_COLUMN_ORDER':
            if (state.type === 'ready') {
                state.columnOrder.value = action.value;
            }
            return state;
        case 'UPDATE_COLUMN_VISIBILITY':
            if (state.type === 'ready') {
                state.columnVisibility.value = action.value;
            }
            return state;
        case 'UPDATE_CURRENT_SORT':
            if (state.type === 'ready') {
                state.currentSort.value = action.value;
            }
            return state;
        case 'UPDATE_USER_ACCOUNTS':
            if (state.type === 'initializing') {
                const newState = {
                    ...state,
                    userAccounts: action.value,
                };

                // Use !== null instead of truthiness checks: these fields are arrays that
                // may legitimately be empty, and an empty array is falsy.
                // `providers` uses a truthiness check because its value is always
                // a non-nullable LocateProvider[] (the effect guards with `if (data)`),
                // so it is either null (not yet loaded) or a populated array — never empty.
                // quoteProviders is derived from providers in the UPDATE_PROVIDERS case,
                // so it is always set when providers is set.
                if (newState.locatesAccounts !== null && newState.providers && newState.userAccounts !== null) {
                    return makeLocatesV2ReadyWidgetState(newState);
                }

                return newState;
            }

            state.userAccounts.value = action.value;
            return state;
        case 'UPDATE_LOCATES_ACCOUNTS':
            if (state.type === 'initializing') {
                const newState = {
                    ...state,
                    locatesAccounts: action.value,
                };

                if (newState.locatesAccounts !== null && newState.providers && newState.userAccounts !== null) {
                    return makeLocatesV2ReadyWidgetState(newState);
                }

                return newState;
            }

            state.locatesAccounts.value = action.value;
            return state;
        case 'UPDATE_PROVIDERS':
            if (state.type === 'initializing') {
                const quoteProviders = action.value.filter((p) => p.supportsQuotes);
                const newState = {
                    ...state,
                    providers: action.value,
                    quoteProviders,
                };

                if (newState.locatesAccounts !== null && newState.providers && newState.userAccounts !== null) {
                    return makeLocatesV2ReadyWidgetState(newState);
                }

                return newState;
            }

            state.providers.value = action.value;
            state.quoteProviders.value = action.value.filter((p) => p.supportsQuotes);
            return state;
        case 'UPDATE_AUTO_ACCEPT_TYPE':
            state.autoAcceptType.value = action.value;
            return state;
        case 'UPDATE_AUTO_ACCEPT_PERCENT':
            state.autoAcceptPercent.value = action.value;
            return state;
        default:
            return state;
    }
}

@injectable()
export class LocatesV2WidgetModelImpl extends ReactiveInjectable implements LocatesV2WidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;

    #locatesAccountsQuery: QueryObserverModel<LocateAccount[]>;
    #locatesProvidersQuery: QueryObserverModel<LocateProvider[]>;
    #rowDataSourceProvider!: LocatesRowDataSourceWorkerProvider;
    #socketConnectionStatus!: ReadonlySignal<TableStatus>;
    #state!: Signal<LocatesV2WidgetModelState>;
    #devtools: ReducerDevtoolsConnection<LocatesV2WidgetModelState, LocatesV2WidgetModelAction> | undefined;
    #store: ReactBindings['Store'];
    #userAccountsQuery: QueryObserverModel<ConcreteAccount[]>;
    #clickedOrderIds: Signal<Set<string>>;
    #filterValue: Signal<string>;
    #filterIsPinned: Signal<boolean>;
    #columnFilters: Signal<ColumnFilters>;
    #isAutoAcceptForSession: Signal<boolean>;
    #activeUserModel: ReactBindings['ActiveUserModel'];
    #autoAcceptFeatureFlagEnabled: ReadonlySignal<boolean>;
    #isAutoAcceptEnabled: ReadonlySignal<boolean>;
    #autoAcceptPercentFeatureFlagEnabled: ReadonlySignal<boolean>;
    #userActivityTabFeatureFlagEnabled: ReadonlySignal<boolean>;
    #shouldSubscribeMarketData: Signal<boolean>;
    #createOrderMutation: MutationObserver<LocateOrder[], Error, CreateLocatesOrderParams, unknown>;
    readonly locatesInjection: LocatesInjectionModel;
    #isStandaloneMode: boolean = false;
    #standaloneDefaults!: LocatesV2WidgetViewModel;
    #instanceId = crypto.randomUUID().slice(0, 8);

    // New header system signals
    #isAccountSelectionValid: Signal<boolean> = signal(true);
    #connectionStatus: Signal<
        ConnectionStatusSignal['widget::connectionStatus'] extends ReadonlySignal<infer T> ? T : never
    > = signal({
        status: 'uninitialized' as TableStatus,
        progress: undefined,
        failureCount: 0,
    });

    headerElements: WidgetHeaderElement[] = [
        ...COMMON_HEADER_ELEMENTS,
        { type: 'stickyFilter', position: 30, priority: 6 },
        { type: 'locatesInject', mode: 'single', key: 'single', position: 50, priority: 8 },
        { type: 'locatesInject', mode: 'csv', key: 'csv', position: 51, priority: 8 },
        { type: 'locatesSettingsMenu', position: 52, priority: 7 },
        { type: 'accounts', position: 55, priority: 5, selectType: 'multi' },
    ];

    eventBus!: LocatesV2WidgetModel['eventBus'];
    readonly #locatesWorkerConnection: Pick<ReactBindings['LocatesWorkerConnection'], 'acquire'>;

    constructor(
        @inject('Store') store: ReactBindings['Store'],
        @inject('WidgetDataModel') @reacts private widgetData: ReactBindings['WidgetDataModel'],
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('ActiveUserModel') activeUserModel: ReactBindings['ActiveUserModel'],
        @inject('SlugMapServiceProvider')
        private readonly slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('TableModel') private readonly marketDataTableModel: ReactBindings['TableModel'],
        @inject('FeatureFlagService') featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('LocatesWorkerConnection')
        locatesWorkerConnection: Pick<ReactBindings['LocatesWorkerConnection'], 'acquire'>,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
        @inject('MessagesService') @optional() private readonly messagesService?: MessagesService,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#store = store;
        this.#locatesWorkerConnection = locatesWorkerConnection;
        this.#activeUserModel = activeUserModel;
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;

        // Read feature flags
        this.#autoAcceptFeatureFlagEnabled = featureFlagService.getSignal(enableLab2AutoAccept);
        this.#autoAcceptPercentFeatureFlagEnabled = featureFlagService.getSignal(enableLabAutoAcceptPercent);
        this.#userActivityTabFeatureFlagEnabled = featureFlagService.getSignal(locatesV2UserOnlyFiltering);
        // Initialize to false - will be updated by effect once state is ready
        this.#shouldSubscribeMarketData = signal<boolean>(false);

        this.#clickedOrderIds = signal<Set<string>>(new Set());
        this.#filterValue = signal<string>('');
        this.#filterIsPinned = signal<boolean>(false);
        this.#columnFilters = signal<ColumnFilters>({});
        this.#isAutoAcceptForSession = signal<boolean>(false);
        this.#userAccountsQuery = queryObserverModelFactory(getAccountsForUserQuery('reader'));
        this.#locatesAccountsQuery = queryObserverModelFactory(getLocatesAccountsQuery());
        this.#locatesProvidersQuery = queryObserverModelFactory(getLocatesProvidersQuery());
        this.#createOrderMutation = new MutationObserver(queryClient, createLocatesOrderMutation());
        this.locatesInjection = this.disposableStack.use(
            new LocatesInjectionModel({
                instanceId: this.#instanceId,
                logger: log,
                queryClient,
                accounts: computed(() => {
                    const state = this.#state?.value;
                    return state?.type === 'ready' ? state.combinedAccounts.value : EmptyArray;
                }),
                providers: computed(() => {
                    const state = this.#state?.value;
                    return state?.type === 'ready' ? state.providers.value : EmptyArray;
                }),
                preferredAccountIds: computed(() => {
                    const state = this.#state?.value;
                    return state?.type === 'ready' ? (state.accountIds.value ?? EmptyArray) : EmptyArray;
                }),
                onInjected: (orders) => this.#rowDataSourceProvider.upsertOrders(orders),
                onSuccess: (count) => {
                    this.#store.dispatch({
                        type: 'toast::enqueue',
                        toast: {
                            message: `${count} ${count === 1 ? 'locate' : 'locates'} injected`,
                            group: 'default',
                            variant: 'success',
                        },
                    });
                },
            }),
        );

        // Create computed signal for auto-accept enabled
        // Requires both the feature flag to be enabled AND the user to have the specific role
        this.#isAutoAcceptEnabled = computed(() => {
            const featureFlagEnabled = this.#autoAcceptFeatureFlagEnabled.value;

            // if flag is off, roles and super user do not matter
            if (!featureFlagEnabled) {
                return false;
            }

            // if super user mode is enabled is on roles do not matter
            if (this.#activeUserModel.isSuperUserModeEnabled.value) {
                return true;
            }

            // check for user role
            const userRoles = this.#activeUserModel.userRoles.value;
            const hasRequiredRole = userRoles.includes('locates::allow-auto-accept');
            return hasRequiredRole;
        });

        // NOTE: Query-watching effects are set up in init() after #state is created,
        // not here in the constructor. If they ran here, cached query data would cause
        // effects to fire synchronously before #state exists, silently dropping the
        // updates and leaving the widget stuck in 'initializing' forever.

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        // Build the EventBus for the new header system
        this.eventBus = this.#buildEventBus();
    }

    #publishLocatesActivity(activity: LocatesActivity): void {
        const isAccepted = activity.resultState === 'accepted';
        const priceText = activity.price === null ? '' : ` at ${activity.price}`;
        const reasonText = activity.rejectReason ? `: ${activity.rejectReason}` : '';
        const baseMessage = {
            source: 'locates' as const,
            id: activity.id,
            accountId: activity.accountId,
            timestamp: activity.timestamp,
            message: `${isAccepted ? 'Accepted' : 'Rejected'} ${activity.orderQuantity} ${activity.symbol}${priceText}${reasonText}`,
            symbol: activity.symbol,
            activityUserId: activity.activityUserId,
        };

        if (isAccepted) {
            this.messagesService?.publish({
                ...baseMessage,
                type: 'accepted',
                typeLabel: 'LAB Accepted',
            });
            return;
        }

        this.messagesService?.publish({
            ...baseMessage,
            type: 'rejected',
            typeLabel: 'LAB Rejected',
        });
    }

    /**
     * Builds the EventBus for the new widget header system.
     * Combines widget-specific signals/events with slice-provided ones.
     */
    #buildEventBus(): LocatesV2WidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Locates-specific signals
                .addSignal('widget::selectedAccountIds', () =>
                    computed(() => {
                        const state = this.#state?.value;
                        if (state?.type !== 'ready') return EmptyArray as string[];
                        return state.accountIds.value ?? (EmptyArray as string[]);
                    }),
                )
                .addSignal('widget::isAccountSelectionValid', () => this.#isAccountSelectionValid)
                .addSignal('widget::stickyFilterValue', () => this.#filterValue)
                .addSignal('widget::isStickyFilterSticky', () => this.#filterIsPinned)
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addSignal('widget::canInjectLocates', () => this.locatesInjection.canInject)
                .addSignal('widget::locatesSoundAlertEnabled', () =>
                    computed(() => {
                        const state = this.#state?.value;
                        if (state?.type !== 'ready') return false;
                        return state.isSoundAlertEnabled.value;
                    }),
                )
                .addSignal('widget::locatesQuantityKilo', () =>
                    computed(() => {
                        const state = this.#state?.value;
                        if (state?.type !== 'ready') return false;
                        return state.isQuantityKilo.value;
                    }),
                )
                .addSignal('widget::locatesUserOnly', () =>
                    computed(() => {
                        const state = this.#state?.value;
                        if (state?.type !== 'ready') return false;
                        return state.isUserOnly.value;
                    }),
                )
                // Locates-specific events
                .addEvent('widget::instrumentChange', (_payload) => {
                    // Locates widget doesn't use instrument
                })
                .addEvent('widget::updateStickyFilter', (payload) => {
                    this.setFilterValue(payload.filter);
                })
                .addEvent('widget::toggleStickyFilter', () => {
                    this.toggleFilterPinned();
                })
                .addEvent('widget::updateSelectedAccountIds', (payload) => {
                    this.setAccountIds(payload.accountIds);
                })
                .addEvent('widget::updateSelectedAccountId', (payload) => {
                    this.setAccountIds([payload.accountId]);
                })
                .addEvent('widget::setIsAccountSelectionValid', (payload) => {
                    this.#isAccountSelectionValid.value = payload.isValid;
                })
                .addEvent('widget::locatesToggleSoundAlert', (payload) => {
                    this.setSoundAlert(payload.enabled);
                })
                .addEvent('widget::locatesToggleQuantityKilo', (payload) => {
                    this.setQuantityKilo(payload.enabled);
                })
                .addEvent('widget::locatesToggleUserOnly', (payload) => {
                    this.setUserOnly(payload.enabled);
                })
                .addEvent('widget::openLocatesInjectDialog', ({ mode }) => {
                    this.locatesInjection.open(mode);
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus);
    }

    async init(tabId: string): Promise<void> {
        // Detect standalone mode - when tabId is 'standalone', state is not persisted to Redux
        this.#isStandaloneMode = tabId === 'standalone';

        // Initialize slices for the new widget header system (needed even in standalone mode
        // because the eventBus merges with slice eventBuses)
        this.#channelSlice.init(tabId);
        this.#widgetTabSlice.init(tabId);
        this.disposableStack.use(this.#channelSlice);
        this.disposableStack.use(this.#widgetTabSlice);

        if (this.#isStandaloneMode) {
            // In standalone mode, use defaults instead of Redux state
            this.#standaloneDefaults = createDefaultLocatesV2Widget();
            this.#filterIsPinned.value = false;
            this.#filterValue.value = '';
        } else {
            // In widget mode, initialize Redux sync
            this.widgetData.init(tabId);

            // Initialize filter state from Redux
            // If Redux has a filter (non-null), it's pinned. Otherwise, it's unpinned.
            const reduxFilter = this.#widget.filter;
            this.#filterIsPinned.value = reduxFilter !== null;
            this.#filterValue.value = reduxFilter ?? '';
        }

        // Create initial filters from widget state (Redux or defaults).
        // Gate persisted `onlyUserActivity` behind the feature flag so a stale `true` value
        // can't silently filter results when the User Activity tab itself is disabled.
        const initialFilters = {
            textSearch: this.#widget.filter,
            onlyPending: this.#widget.onlyPending,
            onlyResults: this.#widget.onlyResults,
            onlyUserActivity: this.#userActivityTabFeatureFlagEnabled.value && this.#widget.onlyUserActivity,
            currentUserId: this.#activeUserModel.userId.value ?? null,
            selectedAccountIds: null, // Will be set by the effect once state is ready
        };

        // Note: Accounts will be passed once they're loaded (see effect below)
        this.#rowDataSourceProvider = this.disposableStack.use(
            new LocatesRowDataSourceWorkerProvider(
                this.queryClient,
                initialFilters,
                this.slugMapServiceProvider,
                this.marketDataTableModel,
                this.#shouldSubscribeMarketData,
                this.#locatesWorkerConnection,
                undefined, // accounts
                this.#instanceId,
                (activity) => this.#publishLocatesActivity(activity),
            ),
        );

        // Bind the public socket-connection-status to the worker provider's signal up
        // front. This guarantees the field is set even when #initializeSocketProvider
        // exits early (e.g. no connection settings available), so consumers reading
        // .value never see undefined.
        this.#socketConnectionStatus = this.#rowDataSourceProvider.socketConnectionStatus;

        // Initial state - reads from #widget which handles standalone vs Redux
        const columnOrder = this.#widget.resultsTableColumns?.columnOrder ?? DEFAULT_COLUMN_ORDER;
        const columnWidths = this.#widget.resultsTableColumns?.columnWidths ?? DEFAULT_COLUMN_WIDTHS;
        const columnVisibility = this.#widget.resultsTableColumns?.columnVisibility ?? DEFAULT_COLUMN_VISIBILITY;
        const currentSort: LocatesV2Sort = this.#widget.resultsTableColumns?.currentSort ?? DEFAULT_LOCATES_V2_SORT;

        this.#state = signal<LocatesV2WidgetModelState>({
            type: 'initializing',
            userAccounts: null,
            locatesAccounts: null,
            providers: null,
            quoteProviders: null,
            formData: signal<LocatesV2FormData>(this.#widget.formData),
            quickSendButtons: signal<QuickSendButton[]>(this.#widget.quickSendButtons),
            isSoundAlertEnabled: signal<boolean>(this.#widget.isSoundAlertEnabled),
            isQuantityKilo: signal<boolean>(this.#widget.isQuantityKilo),
            isUserOnly: signal<boolean>(this.#widget.isUserOnly),
            onlyPending: signal<boolean>(this.#widget.onlyPending),
            onlyResults: signal<boolean>(this.#widget.onlyResults),
            onlyUserActivity: signal<boolean>(this.#widget.onlyUserActivity),
            accountIds: signal<string[] | null>(this.#widget.accountIds),
            filter: signal<string | null>(this.#widget.filter),
            costInfoColumnTemplate: signal<ColumnTemplate | string | null>(
                this.#widget.costInfoTableData.columnTemplate,
            ),
            combinedAccounts: signal<LocateAccount[]>([]),
            selectedAccounts: signal<LocateAccount[]>([]),
            readerPermissionAccounts: signal<LocateAccount[]>([]),
            nonLocatesAccountIds: signal<string[]>([]),
            splitPercentage: signal<number>(this.#widget.splitPercentage),
            isShowingQuote: signal<boolean>(this.#widget.displayingQuote),
            isShowingResults: signal<boolean>(this.#widget.displayingResults),
            isShowingCostInfo: signal<boolean>(this.#widget.displayingCostInfo),
            columnOrder: signal<string[]>(columnOrder),
            columnWidths: signal<Record<string, number>>(columnWidths),
            columnVisibility: signal<Record<string, boolean>>(columnVisibility),
            currentSort: signal<LocatesV2Sort>(currentSort),
            autoAcceptType: signal<'price' | 'percent'>(this.#widget.autoAcceptType ?? 'percent'),
            autoAcceptPercent: signal<number>(this.#widget.autoAcceptPercent ?? 1),
        });

        this.#devtools = connectReducerDevtools({ name: `LocatesV2 [${tabId}]` });
        if (this.#devtools) {
            this.disposableStack.use(this.#devtools);
            this.#devtools.init(this.#state.value);
        }

        // Set up query-watching effects here (after #state exists) so that cached
        // query data is dispatched into the reducer immediately rather than being
        // silently dropped by the !#state guard in #updateState.
        //
        // Each query watcher uses a deep-equal-guarded signal rather than a plain
        // computed off `.data`.  While TanStack Query's structural sharing normally
        // preserves data references across refetch notifications, a guarded signal
        // is strictly more robust: if structural sharing ever produces a new reference
        // for unchanged data, the deep-equal check prevents the cascade of downstream
        // work (state updates, data-source recreation, filter re-evaluation, etc.).
        // This is not idiomatic signal usage — in theory a clean computed chain would
        // suffice — but given the severity of the bug (browser freeze) the explicit
        // comparison is a worthwhile safety net.
        //
        // Error effects intentionally read the full query signal — errors are
        // infrequent and we want to log them regardless of data changes.

        // --- User accounts ---
        this.disposableStack.defer(
            effect(() => {
                const error = this.#userAccountsQuery.signal.value.error;
                if (error) {
                    log.error('userAccounts query failed, widget will not initialize', {
                        instanceId: this.#instanceId,
                        error,
                    });
                }
            }),
        );
        const userAccountsData = signal<ConcreteAccount[] | undefined>(undefined);
        this.disposableStack.defer(
            effect(() => {
                const data = this.#userAccountsQuery.signal.value.data;
                if (data && !isEqual(data, userAccountsData.peek())) {
                    userAccountsData.value = data;
                }
            }),
        );
        this.disposableStack.defer(
            effect(() => {
                const data = userAccountsData.value;
                if (data) {
                    this.#updateState({ type: 'UPDATE_USER_ACCOUNTS', value: data });
                }
            }),
        );

        // --- Locates accounts ---
        this.disposableStack.defer(
            effect(() => {
                const error = this.#locatesAccountsQuery.signal.value.error;
                if (error) {
                    log.error('locatesAccounts query failed, widget will not initialize', {
                        instanceId: this.#instanceId,
                        error,
                    });
                }
            }),
        );
        const locatesAccountsData = signal<LocateAccount[] | undefined>(undefined);
        this.disposableStack.defer(
            effect(() => {
                const data = this.#locatesAccountsQuery.signal.value.data;
                if (data && !isEqual(data, locatesAccountsData.peek())) {
                    locatesAccountsData.value = data;
                }
            }),
        );
        this.disposableStack.defer(
            effect(() => {
                const data = locatesAccountsData.value;
                if (data) {
                    this.#updateState({ type: 'UPDATE_LOCATES_ACCOUNTS', value: data });
                    // Update provider with accounts for account name sorting
                    // Defer this to avoid signal cycle during effect execution
                    if (this.#rowDataSourceProvider && 'updateAccounts' in this.#rowDataSourceProvider) {
                        untracked(() => {
                            this.#rowDataSourceProvider.updateAccounts(data);
                        });
                    }
                }
            }),
        );

        // --- Providers ---
        this.disposableStack.defer(
            effect(() => {
                const error = this.#locatesProvidersQuery.signal.value.error;
                if (error) {
                    log.error('providers query failed, widget will not initialize', {
                        instanceId: this.#instanceId,
                        error,
                    });
                }
            }),
        );
        const providersData = signal<LocateProvider[] | undefined>(undefined);
        this.disposableStack.defer(
            effect(() => {
                const data = this.#locatesProvidersQuery.signal.value.data;
                if (data && !isEqual(data, providersData.peek())) {
                    providersData.value = data;
                }
            }),
        );
        this.disposableStack.defer(
            effect(() => {
                const data = providersData.value;
                if (data) {
                    this.#updateState({ type: 'UPDATE_PROVIDERS', value: data });
                    // Update provider with providers for provider name column filtering
                    // Defer this to avoid signal cycle during effect execution
                    if (this.#rowDataSourceProvider && 'updateProviders' in this.#rowDataSourceProvider) {
                        untracked(() => {
                            this.#rowDataSourceProvider.updateProviders(data);
                        });
                    }
                }
            }),
        );

        {
            const currentState = this.#state.value;
            if (currentState.type === 'initializing') {
                log.info('Query-watching effects set up, still initializing', {
                    instanceId: this.#instanceId,
                    hasUserAccounts: currentState.userAccounts !== null,
                    hasLocatesAccounts: currentState.locatesAccounts !== null,
                    hasProviders: currentState.providers !== null,
                });
            } else {
                log.info('Query-watching effects set up, already ready (queries were cached)', {
                    instanceId: this.#instanceId,
                });
            }
        }

        // Only subscribe to Redux changes in widget mode (not standalone)
        if (!this.#isStandaloneMode) {
            this.disposableStack.use(
                from(this.widgetData).subscribe(() => {
                    const widget = this.widgetData.widget as LocatesV2WidgetViewModel;

                    // Only update if state is ready
                    if (this.#state.value.type === 'ready') {
                        batch(() => {
                            // Update internal state when Redux values change
                            this.#updateState({ type: 'UPDATE_DISPLAYING_QUOTE', value: widget.displayingQuote });
                            this.#updateState({ type: 'UPDATE_DISPLAYING_RESULTS', value: widget.displayingResults });
                            this.#updateState({
                                type: 'UPDATE_DISPLAYING_COST_INFO',
                                value: widget.displayingCostInfo,
                            });
                            this.#updateState({ type: 'UPDATE_SPLIT_PERCENTAGE', value: widget.splitPercentage });
                            this.#updateState({ type: 'UPDATE_FORM_DATA', value: widget.formData });
                            this.#updateState({ type: 'UPDATE_QUICK_SEND_BUTTONS', value: widget.quickSendButtons });
                            this.#updateState({ type: 'UPDATE_SOUND_ALERT', value: widget.isSoundAlertEnabled });
                            this.#updateState({ type: 'UPDATE_QUANTITY_KILO', value: widget.isQuantityKilo });
                            this.#updateState({ type: 'UPDATE_USER_ONLY', value: widget.isUserOnly });
                            this.#updateState({ type: 'UPDATE_ONLY_PENDING', value: widget.onlyPending });
                            this.#updateState({ type: 'UPDATE_ONLY_RESULTS', value: widget.onlyResults });
                            this.#updateState({
                                type: 'UPDATE_ONLY_USER_ACTIVITY',
                                value: widget.onlyUserActivity,
                            });
                            this.#updateState({ type: 'UPDATE_ACCOUNT_IDS', value: widget.accountIds });
                            this.#updateState({ type: 'UPDATE_FILTER', value: widget.filter });
                            this.#updateState({
                                type: 'UPDATE_COST_INFO_COLUMN_TEMPLATE',
                                value: widget.costInfoTableData.columnTemplate,
                            });
                            if (widget.resultsTableColumns) {
                                if (widget.resultsTableColumns.columnOrder) {
                                    this.#updateState({
                                        type: 'UPDATE_COLUMN_ORDER',
                                        value: widget.resultsTableColumns.columnOrder,
                                    });
                                }
                                if (widget.resultsTableColumns.columnWidths) {
                                    this.#updateState({
                                        type: 'UPDATE_COLUMN_WIDTHS',
                                        value: widget.resultsTableColumns.columnWidths,
                                    });
                                }
                                if (widget.resultsTableColumns.columnVisibility) {
                                    this.#updateState({
                                        type: 'UPDATE_COLUMN_VISIBILITY',
                                        value: widget.resultsTableColumns.columnVisibility,
                                    });
                                }
                                if (widget.resultsTableColumns.currentSort) {
                                    this.#updateState({
                                        type: 'UPDATE_CURRENT_SORT',
                                        value: widget.resultsTableColumns.currentSort,
                                    });
                                }
                            }
                            if (widget.autoAcceptType !== undefined) {
                                this.#updateState({
                                    type: 'UPDATE_AUTO_ACCEPT_TYPE',
                                    value: widget.autoAcceptType,
                                });
                            }
                            if (widget.autoAcceptPercent !== undefined) {
                                this.#updateState({
                                    type: 'UPDATE_AUTO_ACCEPT_PERCENT',
                                    value: widget.autoAcceptPercent,
                                });
                            }
                        });
                    }
                }),
            );
        }

        if (this.#state.value.type !== 'ready') {
            const initDeferred = deferred<void>();
            const unsubscribe = effect(() => {
                if (this.#state.value.type === 'ready') {
                    initDeferred.resolve();
                }
            });
            await initDeferred.promise;
            unsubscribe();
        }

        // Initialize socket update provider for real-time updates
        this.#initializeSocketProvider();

        // Sync socket connection status to the EventBus connection status signal
        this.disposableStack.defer(
            effect(() => {
                const socketStatus = this.#socketConnectionStatus?.value ?? 'uninitialized';
                this.#connectionStatus.value = {
                    status: socketStatus,
                    progress: undefined,
                    failureCount: 0,
                };
            }),
        );

        // Watch for filter value and other filter changes, then update provider
        this.disposableStack.defer(
            effect(() => {
                const state = this.#state.value;
                if (state.type === 'ready') {
                    // Use the local filter value for filtering (supports unpinned filters)
                    // The model is the source of truth; Redux is only for persistence
                    const textSearch = this.#filterValue.value || null;
                    const onlyPending = state.onlyPending.value;
                    const onlyResults = state.onlyResults.value;
                    const selectedAccountIds = state.selectedAccounts.value.map((acc) => acc.id);
                    const onlyUserActivity =
                        this.#userActivityTabFeatureFlagEnabled.value && state.onlyUserActivity.value;
                    const currentUserId = this.#activeUserModel.userId.value ?? null;

                    // Per-column filters (session-only, not persisted)
                    const columnFilters = this.#columnFilters.value;

                    const filters = {
                        textSearch,
                        onlyPending,
                        onlyResults,
                        onlyUserActivity,
                        currentUserId,
                        selectedAccountIds,
                        columnFilters,
                    };

                    log.info('Lab filter effect triggered', {
                        instanceId: this.#instanceId,
                        onlyPending,
                        onlyResults,
                        onlyUserActivity,
                        hasCurrentUserId: currentUserId !== null,
                        hasTextSearch: Boolean(textSearch),
                        textSearchLength: textSearch?.length ?? 0,
                        selectedAccountCount: selectedAccountIds.length,
                        hasColumnFilters: Object.keys(columnFilters).length > 0,
                        columnFilterCount: Object.keys(columnFilters).length,
                    });

                    // Update filters in provider (recreates data source)
                    this.#rowDataSourceProvider.updateFilters(filters).catch((error) => {
                        log.error('Failed to update filters in provider', { error, filters });
                    });
                }
            }),
        );

        // Effect to update market data subscription eligibility based on column visibility
        // Only subscribe if at least one of priceChange5m or percentOfPrice columns is visible
        this.disposableStack.defer(
            effect(() => {
                const state = this.#state.value;
                if (state.type !== 'ready') {
                    this.#shouldSubscribeMarketData.value = false;
                    if (TRACE) {
                        log.trace('Market data subscription eligibility: state not ready', {
                            shouldSubscribe: false,
                        });
                    }
                    return;
                }

                const visibility = state.columnVisibility.value;
                const priceChange5mVisible = visibility.priceChange5m === true;
                const percentOfPriceVisible = visibility.percentOfPrice === true;
                const shouldSubscribe = priceChange5mVisible || percentOfPriceVisible;
                this.#shouldSubscribeMarketData.value = shouldSubscribe;

                if (TRACE) {
                    log.trace('Market data subscription eligibility updated', {
                        shouldSubscribe,
                        priceChange5mVisible,
                        percentOfPriceVisible,
                        priceChange5mValue: visibility.priceChange5m,
                        percentOfPriceValue: visibility.percentOfPrice,
                    });
                }
            }),
        );

        // Ensure computed columns are present in the visibility map.
        this.disposableStack.defer(
            effect(() => {
                const state = this.#state.value;

                if (state.type !== 'ready') {
                    if (TRACE) {
                        log.trace('updateColumnVisibility effect: state not ready');
                    }
                    return;
                }

                const currentVisibility = state.columnVisibility.value;
                const hasPriceChange5m = 'priceChange5m' in currentVisibility;
                const hasPercentOfPrice = 'percentOfPrice' in currentVisibility;
                const hasComputedColumns = hasPriceChange5m && hasPercentOfPrice;

                if (!hasComputedColumns) {
                    if (TRACE) {
                        log.trace('updateColumnVisibility effect: adding computed columns', {
                            hasPriceChange5m,
                            hasPercentOfPrice,
                        });
                    }
                    this.updateColumnVisibility({
                        ...currentVisibility,
                        priceChange5m: true,
                        percentOfPrice: true,
                    });
                    return;
                }

                if (TRACE) {
                    log.trace('updateColumnVisibility effect: no change needed', {
                        hasComputedColumns,
                    });
                }
            }),
        );
    }

    #initializeSocketProvider(): void {
        const connectionSettings = getLocatesSocketConnectionSettingsFromState(this.#store.getState());
        if (!connectionSettings) {
            if (TRACE) {
                log.trace('No socket connection settings available, skipping socket provider');
            }
            return;
        }

        // Get account IDs from the ready state to subscribe to room updates
        // This must be called after state is ready since we need the locates accounts
        const state = this.#state.value;
        if (state.type !== 'ready') {
            log.error('Cannot initialize socket provider - state not ready');
            return;
        }

        // Get all locates account IDs to subscribe to updates for all accounts
        const accountIds = state.locatesAccounts.value.map((acc) => acc.id);

        log.info('Initializing socket connection', {
            instanceId: this.#instanceId,
            accountCount: accountIds.length,
        });

        void this.#rowDataSourceProvider.worker
            .request('initializeSocket', {
                connectionSettings,
                accountIds,
                instanceId: this.#instanceId,
            })
            .catch((error: unknown) => log.error('Failed to initialize locates socket', { error }));
    }

    #updateState(action: LocatesV2WidgetModelAction) {
        /*
        if (TRACE) {
            log.trace('LocatesV2WidgetModel state update', { action, prevState: this.#state.value });
        }
        */
        // Guard against race condition where effects fire before init() is called
        if (!this.#state) {
            return;
        }
        // Must use peek() to avoid tracking the state signal in computed/effect
        this.#state.value = locatesV2WidgetModelReducer(this.#state.peek(), action);
        this.#devtools?.send(action, this.#state.peek());
    }

    #viewState = computed(() => {
        if (this.#state.value.type === 'initializing') {
            return { type: 'initializing' } as const;
        }

        return this.#state.value;
    });
    get state(): ReadonlySignal<LocatesV2WidgetModelViewState> {
        return this.#viewState;
    }

    get rowDataSourceProvider(): LocatesRowDataSourceWorkerProvider {
        return this.#rowDataSourceProvider;
    }

    get socketConnectionStatus(): ReadonlySignal<TableStatus> {
        return this.#socketConnectionStatus;
    }

    get clickedOrderIds(): ReadonlySignal<ReadonlySet<string>> {
        return computed(() => this.#clickedOrderIds.value as ReadonlySet<string>);
    }

    get filterValue(): ReadonlySignal<string> {
        return this.#filterValue;
    }

    get filterIsPinned(): ReadonlySignal<boolean> {
        return this.#filterIsPinned;
    }

    get isAutoAcceptEnabled(): ReadonlySignal<boolean> {
        return this.#isAutoAcceptEnabled;
    }

    get isAutoAcceptPercentEnabled(): ReadonlySignal<boolean> {
        return this.#autoAcceptPercentFeatureFlagEnabled;
    }

    get columnFilters(): ReadonlySignal<ColumnFilters> {
        return this.#columnFilters;
    }

    readonly activeColumnFilters = computed(() => getActiveColumnFilters(this.#columnFilters.value));

    get resultsCountLabel(): ReadonlySignal<string> {
        return this.#rowDataSourceProvider.rowCountLabel;
    }

    get isLoadingResults(): ReadonlySignal<boolean> {
        return this.#rowDataSourceProvider.isLoading;
    }

    get marketDataMap(): ReadonlySignal<
        Map<string, { lastPrice: number | undefined; priceChange5m: number | undefined }>
    > {
        return this.#rowDataSourceProvider.marketDataMap;
    }

    addClickedOrderId(orderId: string): void {
        const current = this.#clickedOrderIds.peek();
        this.#clickedOrderIds.value = new Set([...current, orderId]);
    }

    removeClickedOrderId(orderId: string): void {
        const current = this.#clickedOrderIds.peek();
        const next = new Set(current);
        next.delete(orderId);
        this.#clickedOrderIds.value = next;
    }

    setShowingQuote(value: boolean): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_DISPLAYING_QUOTE', value });
        } else {
            this.#store.dispatch(setLocatesV2DisplayingQuote(this.widgetData.tabId, value));
        }
    }

    setShowingResults(value: boolean): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_DISPLAYING_RESULTS', value });
        } else {
            this.#store.dispatch(setLocatesV2DisplayingResults(this.widgetData.tabId, value));
        }
    }

    setShowingCostInfo(value: boolean): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_DISPLAYING_COST_INFO', value });
        } else {
            // Dispatch to Redux - internal state will update via subscription
            this.#store.dispatch(setLocatesV2DisplayingCostInfo(this.widgetData.tabId, value));
        }
    }

    setSplitPercentage(value: number): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_SPLIT_PERCENTAGE', value });
        } else {
            this.#store.dispatch(storeLocatesV2SplitPercentage(this.widgetData.tabId, value));
        }
    }

    updateFormData(formData: LocatesV2FormData): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_FORM_DATA', value: formData });
        } else {
            this.#store.dispatch(updateLocatesV2FormData(this.widgetData.tabId, formData));
        }
    }

    updateQuickSendButtons(buttons: QuickSendButton[]): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_QUICK_SEND_BUTTONS', value: buttons });
        } else {
            this.#store.dispatch(updateLocatesV2QuickSendButtons(this.widgetData.tabId, buttons));
        }
    }

    async sendLocatesRequest(
        order: Omit<NewLocateOrder, 'optimisticOrderId'>,
        autoAcceptParams?: AutoAcceptParams,
    ): Promise<void> {
        const state = this.#state.value;
        if (state.type !== 'ready') {
            throw new Error('LocatesV2WidgetModel: sendLocatesRequest called before state is ready');
        }

        // Reject orders whose account is no longer returned by /platforms/discover.
        // The selected account id is persisted on the model (formData.accountId) and can
        // outlive the user's entitlement to it (permissions revoked, account removed,
        // masquerade change), in which case discovery omits it. Submitting anyway would
        // place the order against a stale account, so block it here before any network call.
        const isAccountDiscovered = state.locatesAccounts.value.some((acc) => acc.id === order.accountId);
        if (!isAccountDiscovered) {
            const errorMessage = 'Account is no longer available for locates. Please select a different account.';
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: errorMessage,
                    group: 'default',
                    variant: 'error',
                },
            });
            log.error('Blocked locates order: account not returned by /platforms/discover', {
                instanceId: this.#instanceId,
                accountId: order.accountId,
                discoveredAccountIds: state.locatesAccounts.value.map((acc) => acc.id),
            });
            throw new Error(errorMessage);
        }

        // Multiply quantity by 1000 if isQuantityKilo is enabled
        const quantity = state.isQuantityKilo.value ? order.quantity * 1000 : order.quantity;

        // Construct AutoAcceptInfo based on feature flag and form values
        let autoAccept: AutoAcceptInfo | undefined;
        if (autoAcceptParams?.enabled) {
            const isPercentFeatureEnabled = this.#autoAcceptPercentFeatureFlagEnabled.value;

            if (!isPercentFeatureEnabled) {
                // Legacy mode - no triggers, just price
                autoAccept = {
                    enabled: true,
                    price: autoAcceptParams.price,
                };
            } else if (autoAcceptParams.type === 'price') {
                // Price type with locate-rate trigger
                autoAccept = {
                    enabled: true,
                    // including the price is redundant, but part of the migration plan to triggers
                    // TODO: remove the price field when the migration is complete and Ermolay says it is no longer needed
                    price: autoAcceptParams.price,
                    triggers: [{ type: 'locate-rate', maxRate: autoAcceptParams.price }],
                };
            } else {
                // Percent type with security-price trigger
                // User enters whole numbers (e.g., 1 for 1%), divide by 100 and round to 4 decimal places
                // Validate percent input to prevent NaN, negative, or excessively large values
                const rawPercent = autoAcceptParams.percent;
                if (
                    typeof rawPercent !== 'number' ||
                    isNaN(rawPercent) ||
                    !isFinite(rawPercent) ||
                    rawPercent < 0 ||
                    rawPercent > 10000
                ) {
                    throw new Error(
                        `Invalid percent value: ${rawPercent}. Must be a valid number between 0 and 10000.`,
                    );
                }
                const maxRatePercent = Number((rawPercent / 100).toFixed(4));
                autoAccept = {
                    enabled: true,
                    price: 0, // Required for backward compat, but ignored when triggers present
                    triggers: [{ type: 'security-price', maxRatePercent }],
                };
            }
        }

        try {
            // Call API using React Query mutation (no saga needed for v2)
            this.#createOrderMutation.reset();
            const locateOrders = await this.#createOrderMutation.mutate({
                order: { ...order, quantity },
                autoAccept,
            });

            // Reconcile the API response with socket updates in the worker's canonical store.
            await this.#rowDataSourceProvider.upsertOrders(locateOrders);

            log.info('Order created and reconciled in worker store', {
                instanceId: this.#instanceId,
                orderCount: locateOrders.length,
                firstOrderId: locateOrders[0]?.id,
            });

            if (TRACE) {
                log.trace('Created orders successfully', {
                    realOrderCount: locateOrders.length,
                    realOrderIds: locateOrders.map((o) => o.id),
                });
            }
        } catch (error) {
            // Extract request ID from axios error for debugging
            let requestId: string | undefined;
            if (isAxiosError(error)) {
                const headerRequestId = error.config?.headers?.[requestIdHeader];
                const responseRequestId = (error.response?.data as { requestId?: string } | undefined)?.requestId;
                requestId =
                    typeof headerRequestId === 'string'
                        ? headerRequestId
                        : typeof responseRequestId === 'string'
                          ? responseRequestId
                          : undefined;
            }

            // Look up account and provider names from already-loaded data (safe, no network request)
            const accountName =
                state.userAccounts.value.find((acc) => acc.id === order.accountId)?.name ??
                state.locatesAccounts.value.find((acc) => acc.id === order.accountId)?.name ??
                order.accountId;
            const providerName =
                state.providers.value.find((p) => p.id === order.providerId)?.publicName ?? order.providerId;

            // Show error toast with request ID if available
            const errorMessage = `Creating locates order for ${quantity} ${order.symbol} from ${providerName} with ${accountName} failed`;
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: errorMessage,
                    group: 'default',
                    variant: 'error',
                    requestId,
                },
            });
            log.error('Failed to create order', { error, order, quantity, accountName, providerName, requestId });
            throw error;
        }
    }

    setSoundAlert(value: boolean): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_SOUND_ALERT', value });
        } else {
            this.#store.dispatch(setLocatesV2SoundAlert(this.widgetData.tabId, value));
        }
    }

    setQuantityKilo(value: boolean): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_QUANTITY_KILO', value });
        } else {
            this.#store.dispatch(setLocatesV2QuantityKilo(this.widgetData.tabId, value));
        }
    }

    setUserOnly(value: boolean): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_USER_ONLY', value });
        } else {
            this.#store.dispatch(setLocatesV2UserOnly(this.widgetData.tabId, value));
        }
    }

    setOnlyPending(value: boolean): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_ONLY_PENDING', value });
        } else {
            this.#store.dispatch(setLocatesV2OnlyPending(this.widgetData.tabId, value));
        }
    }

    setOnlyResults(value: boolean): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_ONLY_RESULTS', value });
        } else {
            this.#store.dispatch(setLocatesV2OnlyResults(this.widgetData.tabId, value));
        }
    }

    setOnlyUserActivity(value: boolean): void {
        // Block enabling when the flag is off, but allow clearing so that stale
        // `true` state (e.g. persisted before the flag was disabled) can be reset.
        // LocatesResults.tsx has a useEffect that relies on this clear path.
        if (!this.#userActivityTabFeatureFlagEnabled.value && value) {
            return;
        }
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_ONLY_USER_ACTIVITY', value });
        } else {
            this.#store.dispatch(setLocatesV2OnlyUserActivity(this.widgetData.tabId, value));
        }
    }

    get isUserActivityTabEnabled(): ReadonlySignal<boolean> {
        return this.#userActivityTabFeatureFlagEnabled;
    }

    setAccountIds(accountIds: string[] | null): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_ACCOUNT_IDS', value: accountIds });
        } else {
            this.#store.dispatch(setLocatesV2AccountIds(this.widgetData.tabId, accountIds));
        }
    }

    updateCostInfoColumnTemplate(columnTemplate: ColumnTemplate | string | null): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_COST_INFO_COLUMN_TEMPLATE', value: columnTemplate });
        } else {
            // Dispatch to Redux - internal state will update via subscription
            this.#store.dispatch(updateLocatesV2CostInfoColumnTemplate(this.widgetData.tabId, columnTemplate));
        }
    }

    setFilterValue(value: string): void {
        // Update local filter value
        this.#filterValue.value = value;

        // If filter is pinned and not in standalone mode, persist to Redux immediately
        if (this.#filterIsPinned.value && !this.#isStandaloneMode) {
            this.#store.dispatch(updateLocatesV2Filter(this.widgetData.tabId, value));
        }
    }

    toggleFilterPinned(): void {
        const currentlyPinned = this.#filterIsPinned.value;
        const currentValue = this.#filterValue.value;

        if (this.#isStandaloneMode) {
            // In standalone mode, just toggle the local state for UI consistency
            // No Redux persistence
            this.#filterIsPinned.value = !currentlyPinned;
        } else if (currentlyPinned) {
            // Unpinning: set pinned to false and clear Redux (set to null)
            this.#filterIsPinned.value = false;
            this.#store.dispatch(updateLocatesV2Filter(this.widgetData.tabId, null));
        } else {
            // Pinning: set pinned to true and save current value to Redux
            this.#filterIsPinned.value = true;
            this.#store.dispatch(updateLocatesV2Filter(this.widgetData.tabId, currentValue));
        }
    }

    updateColumnWidths(columnWidths: Record<string, number>): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_COLUMN_WIDTHS', value: columnWidths });
        } else {
            // Dispatch to Redux - internal state will update via subscription
            this.#store.dispatch(updateLocatesV2ColumnWidths(this.widgetData.tabId, columnWidths));
        }
    }

    updateColumnOrder(columnOrder: string[]): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_COLUMN_ORDER', value: columnOrder });
        } else {
            // Dispatch to Redux - internal state will update via subscription
            this.#store.dispatch(updateLocatesV2ColumnOrder(this.widgetData.tabId, columnOrder));
        }
    }

    updateColumnVisibility(columnVisibility: Record<string, boolean>): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_COLUMN_VISIBILITY', value: columnVisibility });
        } else {
            // Dispatch to Redux - internal state will update via subscription
            this.#store.dispatch(updateLocatesV2ColumnVisibility(this.widgetData.tabId, columnVisibility));
        }
    }

    updateSort(sort: LocatesV2Sort): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_CURRENT_SORT', value: sort });
        } else {
            // Dispatch to Redux to persist the sort
            this.#store.dispatch(updateLocatesV2Sort(this.widgetData.tabId, sort));
        }
        // Recreate the data source with the new sort (like "Return to top")
        this.#rowDataSourceProvider.recreateDataSource(sort);
    }

    returnToTop(): void {
        // Get the current sort from state to preserve it when returning to top
        const state = this.#state.value;
        const currentSort = state.type === 'ready' ? state.currentSort.value : DEFAULT_LOCATES_V2_SORT;
        this.#rowDataSourceProvider.recreateDataSource(currentSort);
    }

    async acceptOrder(orderId: string): Promise<void> {
        try {
            const { getLocatesClient } = await import('src/routes/widgets/LocatesWidget/hooks/useLocatesClient');
            const client = await getLocatesClient();
            await client.acceptLocatesOrder(orderId);
            if (TRACE) {
                log.trace('Successfully accepted order', { orderId });
            }
        } catch (e) {
            // Show error toast like V1 sagas
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: `Accepting locates order ${orderId} failed`,
                    group: 'default',
                    variant: 'error',
                },
            });
            log.error('Failed to accept order', { orderId, error: e });
            throw e;
        }
    }

    async rejectOrder(orderId: string): Promise<void> {
        try {
            const { getLocatesClient } = await import('src/routes/widgets/LocatesWidget/hooks/useLocatesClient');
            const client = await getLocatesClient();
            await client.rejectLocatesOrder(orderId);
            if (TRACE) {
                log.trace('Successfully rejected order', { orderId });
            }
        } catch (e) {
            // Show error toast like V1 sagas
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: `Rejecting locates order ${orderId} failed`,
                    group: 'default',
                    variant: 'error',
                },
            });
            log.error('Failed to reject order', { orderId, error: e });
            throw e;
        }
    }

    async cancelOrder(orderId: string): Promise<void> {
        try {
            const { getLocatesClient } = await import('src/routes/widgets/LocatesWidget/hooks/useLocatesClient');
            const client = await getLocatesClient();
            await client.cancelLocatesOrder(orderId);
            if (TRACE) {
                log.trace('Successfully cancelled order', { orderId });
            }
        } catch (e) {
            // Show error toast like V1 sagas
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: `Cancelling locates order ${orderId} failed`,
                    group: 'default',
                    variant: 'error',
                },
            });
            log.error('Failed to cancel order', { orderId, error: e });
            throw e;
        }
    }

    async exportCsv(): Promise<void> {
        try {
            const state = this.#state.value;
            if (state.type !== 'ready') {
                log.error('Cannot export CSV - state not ready');
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: 'Cannot export CSV - data not ready',
                        group: 'default',
                        variant: 'error',
                    },
                });
                return;
            }

            // Show initial toast
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: 'Creating CSV export of LAB orders...',
                    group: 'default',
                    variant: 'info',
                    autoHideDuration: null, // persist: true
                },
            });

            log.info('Starting CSV export');

            const columnOrder = state.columnOrder.value;
            const columnVisibility = state.columnVisibility.value;

            const worker = this.#rowDataSourceProvider.worker;
            const response = await worker.request(
                'exportCsv',
                { columnOrder, columnVisibility },
                { signal: AbortSignal.timeout(60_000) },
            );
            if (!response.success) throw new Error(response.error);
            const { csvData, rowCount } = response;

            // Handle empty result as normal outcome
            if (rowCount === 0) {
                log.info('CSV export completed with no rows');
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: 'No rows to export',
                        group: 'default',
                        variant: 'info',
                    },
                });
                return;
            }

            if (!csvData) {
                throw new Error('No data to export');
            }

            // Create blob and download
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `lab_orders_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            log.info('CSV export completed', { rowCount });

            // Update toast
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: `CSV export complete - ${rowCount} rows downloaded`,
                    group: 'default',
                    variant: 'success',
                },
            });
        } catch (e) {
            log.error('Failed to export CSV', { error: e });
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: 'Failed to export CSV',
                    group: 'default',
                    variant: 'error',
                },
            });
            throw e;
        }
    }

    setAutoAcceptType(type: 'price' | 'percent'): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_AUTO_ACCEPT_TYPE', value: type });
        } else {
            this.#store.dispatch(setLocatesV2AutoAcceptType(this.widgetData.tabId, type));
        }
    }

    setAutoAcceptPercent(value: number): void {
        if (this.#isStandaloneMode) {
            this.#updateState({ type: 'UPDATE_AUTO_ACCEPT_PERCENT', value });
        } else {
            this.#store.dispatch(setLocatesV2AutoAcceptPercent(this.widgetData.tabId, value));
        }
    }

    get isAutoAcceptForSession(): ReadonlySignal<boolean> {
        return this.#isAutoAcceptForSession;
    }

    setAutoAcceptForSession(value: boolean): void {
        this.#isAutoAcceptForSession.value = value;
    }

    get #widget(): LocatesV2WidgetViewModel {
        if (this.#isStandaloneMode) {
            return this.#standaloneDefaults;
        }
        return this.widgetData.widget as LocatesV2WidgetViewModel;
    }

    addColumnFilter(columnId: LocatesV2ColumnId, filter: ColumnFilter): void {
        this.#columnFilters.value = columnFilterReducer(this.#columnFilters.peek(), addColumnFilter(columnId, filter));
    }

    removeColumnFilter(columnId: LocatesV2ColumnId, filter: ColumnFilter): void {
        this.#columnFilters.value = columnFilterReducer(
            this.#columnFilters.peek(),
            removeColumnFilter(columnId, filter),
        );
    }

    clearColumnFilter(columnId: LocatesV2ColumnId): void {
        this.#columnFilters.value = columnFilterReducer(this.#columnFilters.peek(), clearColumnFilter(columnId));
    }

    clearAllColumnFilters(): void {
        this.#columnFilters.value = columnFilterReducer(this.#columnFilters.peek(), clearAllColumnFilters());
    }
}
